import {
  Configuration,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser'
import { ssoAPI } from 'api/ssoAPI'
import {
  DEFAULT_B2C_LOGIN_POLICY,
  getB2CAuthority,
  getStoredLoginPolicy,
} from 'helpers/authFlow'
import { getMsalPostLogoutRedirectUri } from 'helpers/logoutRedirect'
import { isMobile } from 'react-device-detect'

let msalInstancePromise: Promise<PublicClientApplication> | null = null

export const getRedirectURI = async () => {
  try {
    if (isMobile) {
      const response = await ssoAPI.getMobileClient(window.location.origin)
      const data = response?.data
      const mobileStart = data?.mobileUrl
      return mobileStart
    } else {
      return window.location.origin
    }
  } catch (e) {
    console.log('isMobile Error ', e)
    return window.location.origin
  }
}

let policy = getStoredLoginPolicy()

if (!policy) {
  try {
    localStorage.setItem('loginPolicy', DEFAULT_B2C_LOGIN_POLICY)
  } catch {
    // Ignore storage failures; MSAL login requests will pass explicit authority.
  }
  policy = DEFAULT_B2C_LOGIN_POLICY
}

const emailHint = localStorage.getItem('emailHint')
const emailDomain = emailHint?.split('@')[1]
const environmentDetails = process.env.REACT_APP_B2C_ENVIRONMENTDETAILS
export const userScopeAPI = `https://jyrneyb2c${environmentDetails}.onmicrosoft.com/users-api/Users.All`

export const b2cPolicies = {
  names: {
    signUpSignIn: policy,
    forgotPassword: 'B2C_1_reset_v3',
    editProfile: 'B2C_1_edit_profile_v2',
  },
  authorities: {
    signUpSignIn: {
      authority: `https://jyrneyb2c${environmentDetails}.b2clogin.com/jyrneyb2c${environmentDetails}.onmicrosoft.com/${policy}`,
    },
    forgotPassword: {
      authority:
        'https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/B2C_1_reset_v3',
    },
    editProfile: {
      authority:
        'https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/b2c_1_edit_profile_v2',
    },
  },
  authorityDomain: `jyrneyb2c${environmentDetails}.b2clogin.com`,
}

export async function buildMsalConfig(): Promise<Configuration> {
  const redirectUri = await getRedirectURI()
  const postLogoutRedirectUri = getMsalPostLogoutRedirectUri()
  const signInPolicy = getStoredLoginPolicy()

  return {
    auth: {
      clientId: `${process.env.REACT_APP_MSALCONFIG_AUTH_CLIENTID}`,
      authority: getB2CAuthority(signInPolicy),
      knownAuthorities: [b2cPolicies.authorityDomain],
      redirectUri,
      postLogoutRedirectUri,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
        loggerCallback: (
          level: LogLevel,
          message: string,
          containsPii: boolean,
        ) => {
          if (containsPii) return
          switch (level) {
            case LogLevel.Error:
              console.error(message)
              return
            case LogLevel.Info:
              console.info(message)
              return
            case LogLevel.Verbose:
              console.debug(message)
              return
            case LogLevel.Warning:
              console.warn(message)
              return
            default:
              return
          }
        },
      },
    },
  }
}
export const msalConfigPromise = buildMsalConfig()

export function getMsalInstance(): Promise<PublicClientApplication> {
  if (!msalInstancePromise) {
    msalInstancePromise = (async () => {
      const config = await msalConfigPromise
      const instance = new PublicClientApplication(config)
      await instance.initialize()

      // Add event callbacks to track auth flows
      instance.addEventCallback((event) => {
        console.log('[MSAL Event]', {
          eventType: event.eventType,
          interactionType: event.interactionType,
          payload: event.payload,
          error: event.error,
          timestamp: new Date().toISOString(),
        })

        // Log specific events of interest
        if (event.eventType === 'msal:acquireTokenStart') {
          console.log('[MSAL] acquireTokenStart', event.interactionType)
        } else if (event.eventType === 'msal:acquireTokenSuccess') {
          console.log('[MSAL] acquireTokenSuccess:', event.interactionType)
        } else if (event.eventType === 'msal:acquireTokenFailure') {
          console.error(
            '[MSAL] acquireTokenFailure:',
            event.interactionType,
            event.error,
          )
        } else if (event.eventType === 'msal:loginStart') {
          console.log('[MSAL] loginStart:', event.interactionType)
        } else if (event.eventType === 'msal:loginSuccess') {
          console.log('[MSAL] loginSuccess:', event.interactionType)
        } else if (event.eventType === 'msal:loginFailure') {
          console.error(
            '[MSAL] loginFailure:',
            event.interactionType,
            event.error,
          )
        }
      })

      return instance
    })()
  }
  return msalInstancePromise
}

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
  apiTodoList: {
    endpoint: `${process.env.REACT_APP_PROTECTEDRESOURCES_ENDPOINT}`,
    scopes: {
      users: [
        `https://jyrneyb2c${environmentDetails}.onmicrosoft.com/users-api/Users.All`,
      ],
    },
  },
}

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */

export const loginRequest = {
  scopes: [
    `https://jyrneyb2c${environmentDetails}.onmicrosoft.com/users-api/Users.All`,
  ],
  domainHint: emailDomain,
  loginHint: emailHint,
}
